import {
  useEffect,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import { Controller } from "react-hook-form";
import type { Moment } from "moment";
import moment from "moment";
import { Calendar } from "../Calendar/Calendar";
import type { MethodsOfUseForm } from "../../types/types";
import { getDateTime } from "../../util/util";

// These keys are required by Calendar, but are not required in the DatePickerProps, because the date picker defines them in
// its implementation, and assigns them to Calendar
// To fix this error, I omit them from the DatePickerProps
type HandledByDatePickerProps =
  | "id"
  | "focused"
  | "date"
  | "handleDateChange"
  | "handleFocusChange"
  | "errors";

interface DatePickerProps
  extends Omit<
    React.ComponentProps<typeof Calendar>,
    HandledByDatePickerProps
  > {
  methodsOfUseForm: MethodsOfUseForm;
  defaultValue?: string | null;
  handleOnChange?: () => void;
  normalPlaceholder?: boolean;
  shouldValidate?: boolean;
  rules?: any;
}

export interface DatePickerHandle {
  updateDate: (date: string) => void;
}

function parsePath(name: string) {
  return name
    .replace(/\[(\d+)\]/g, ".$1") // replace [3] with .3
    .split("."); // split by .
}

function getNestedError(errors: any, name: string) {
  return parsePath(name).reduce((acc: any, part: string) => {
    if (acc == null) return undefined;
    return acc[part];
  }, errors);
}

/**
 * Date picker input element. Wraps the <Calendar> element to encapsulate and
 * reduce repetitive boilerplate related to react-hook-form and react state.
 */
export const DatePicker = forwardRef<DatePickerHandle, DatePickerProps>(
  (
    {
      label,
      name,
      methodsOfUseForm: { control, register, setValue, errors, watch },
      required,
      defaultValue,
      isOutsideRange,
      handleOnChange,
      normalPlaceholder,
      shouldValidate = false,
      rules,
      ...rest
    },
    ref
  ) => {
    const [focused, setFocused] = useState(false);

    const [date, setDate] = useState<Moment | null>(
      defaultValue ? moment.utc(defaultValue) : null
    );

    const calendar_ref = useRef<HTMLDivElement>(null);

    useImperativeHandle(ref, () => ({
      updateDate: (date: string) => {
        const newDate = moment.utc(date);
        if (newDate.isValid()) {
          setDate(newDate);
        }
      },
    }));

    useEffect(() => {
      if (!rules) {
        register({ name, required: !!required });
      }
    });

    useEffect(() => {
      const dateString: string | null = defaultValue
        ? moment.utc(new Date(defaultValue)).toISOString()
        : "";
      setValue(name, dateString || "", { shouldValidate });
    }, [defaultValue, name, register, required, setValue, shouldValidate]);

    const currentValue = watch(name);
    useEffect(() => {
      // Only clear if we have a current value and it's explicitly an empty string
      // This prevents clearing on initial load or reset
      if (currentValue !== undefined && currentValue === "") {
        setDate(null);
        calendar_ref.current?.querySelector("input")?.setAttribute("value", "");
      }
    }, [currentValue]);

    const handleDateChange = (
      date: Moment | null,
      onChange: (value: string) => void
    ) => {
      const input_value = (
        calendar_ref.current?.querySelector(
          ".DateInput_input"
        ) as HTMLInputElement | null
      )?.value;
      setDate(date);
      const dateString = date ? moment.utc(date).toISOString() : "";
      // format the input value to MM/DD/YYYY
      const updated_input_value = input_value
        ? moment.utc(getDateTime(input_value, true).date).toISOString()
        : "";
      onChange(dateString || updated_input_value);
      if (handleOnChange) {
        handleOnChange();
      }
    };

    const handleFocusChange = ({ focused }: { focused: boolean | null }) => {
      setFocused(!!focused);
    };

    return (
      <Controller
        control={control}
        name={name}
        defaultValue={
          defaultValue ? moment.utc(new Date(defaultValue)).toISOString() : ""
        }
        rules={rules}
        render={({ onChange }) => (
          <Calendar
            {...rest}
            label={label}
            date={date}
            focused={focused}
            name={name}
            id={name}
            handleDateChange={(date) => handleDateChange(date, onChange)}
            handleFocusChange={handleFocusChange}
            handleOutsideRange={isOutsideRange}
            normalPlaceholder={normalPlaceholder}
            errors={rules ? { [name]: getNestedError(errors, name) } : errors}
            ref={calendar_ref}
            displayFormat="MMM DD, YYYY"
          />
        )}
      />
    );
  }
);
